/**
 * CRM Authentication Guard
 * Protects CRM routes based on authentication and role requirements
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCrmAuth } from '../../contexts/CrmAuthContext';
import { AccessDenied } from './AccessDenied';
import { isCrmEnabled } from '../../utils/featureFlags';
import { CrmDisabled } from '../../pages/crm/CrmDisabled';

/**
 * Role mapping for routes
 */
const ROLE_REQUIREMENTS = {
  '/crm/admin': ['crm_admin'],
  '/crm/counselor': ['counselor', 'crm_admin'],
  '/crm/student': ['student'],
  '/crm/parent': ['parent'],
};

/**
 * Check if user role has access to a route
 * @param {string} path - Route path
 * @param {string} userRole - User's role
 * @returns {boolean} True if user has access
 */
const hasRoleAccess = (path, userRole) => {
  // Find matching route requirement
  for (const [routePrefix, allowedRoles] of Object.entries(ROLE_REQUIREMENTS)) {
    if (path.startsWith(routePrefix)) {
      return allowedRoles.includes(userRole);
    }
  }
  // Default: allow if no specific requirement
  return true;
};

/**
 * CRM Auth Guard Component
 * @param {object} props
 * @param {React.ReactNode} props.children - Child components to render if authorized
 */
export const CrmAuthGuard = ({ children }) => {
  const { user, profile, loading, isAuthenticated, hasProfile } = useCrmAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If not authenticated, redirect to login
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      navigate('/crm/counselor/login', { state: { from: location.pathname } });
    }
  }, [isAuthenticated, loading, navigate, location.pathname]);

  // If authenticated but no profile, redirect to onboarding (unless counselor/admin)
  useEffect(() => {
    if (!loading && isAuthenticated && user && !hasProfile) {
      const userRole = user.role;
      // Counselor/admin don't need profiles
      if (userRole !== 'counselor' && userRole !== 'crm_admin') {
        navigate('/crm/onboarding');
      }
    }
  }, [loading, isAuthenticated, hasProfile, user, navigate]);

  // Check if CRM is enabled
  if (!isCrmEnabled()) {
    return <CrmDisabled />;
  }

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // If on onboarding page, allow it (onboarding handles its own redirects)
  if (location.pathname === '/crm/onboarding') {
    return <>{children}</>;
  }

  // If no profile and not counselor/admin, show loading while redirecting
  if (!hasProfile && user && user.role !== 'counselor' && user.role !== 'crm_admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Check role-based access
  if (user && !hasRoleAccess(location.pathname, user.role)) {
    return (
      <AccessDenied
        requiredRole={ROLE_REQUIREMENTS[location.pathname]?.[0] || 'Unknown'}
        userRole={user.role}
      />
    );
  }

  // User is authenticated and has correct role
  return <>{children}</>;
};
